"use client";

import { useCallback, useEffect, useState } from "react";

// OneTrust cookie category constants
const ONETRUST_CATEGORIES = {
  STRICTLY_NECESSARY: "snc",
  PERFORMANCE: "per",
  FUNCTIONAL: "fun",
  TARGETING: "tar",
} as const;

declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay?: () => void;
      OnConsentChanged: (callback: () => void) => void;
    };
    OptanonWrapper?: () => void;
    OnetrustActiveGroups?: string;
  }
}

/**
 * Enhanced OneTrust consent hook that returns detailed consent state
 * Provides granular access to all consent categories and convenience methods
 */
export const useOneTrustConsent = () => {
  const [consentState, setConsentState] = useState<{
    isLoading: boolean;
    strictlyNecessaryAllowed: boolean;
    performanceAllowed: boolean;
    functionalAllowed: boolean;
    targetingAllowed: boolean;
    embedsAllowed: boolean;
    activeGroups: string[];
  }>({
    strictlyNecessaryAllowed: true, // Always true by default
    performanceAllowed: false,
    functionalAllowed: false,
    targetingAllowed: false,
    isLoading: true,
    embedsAllowed: false,
    activeGroups: [],
  });

  const updateConsentState = useCallback(() => {
    if (typeof window === "undefined") {
      setConsentState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const activeGroupIds = window.OnetrustActiveGroups || "";
    const activeGroups = activeGroupIds.split(",").filter(Boolean);
    const newState = {
      isLoading: false,
      strictlyNecessaryAllowed: true, // Always true
      performanceAllowed: activeGroups.includes(
        ONETRUST_CATEGORIES.PERFORMANCE,
      ),
      functionalAllowed: activeGroups.includes(ONETRUST_CATEGORIES.FUNCTIONAL),
      targetingAllowed: activeGroups.includes(ONETRUST_CATEGORIES.TARGETING),
      embedsAllowed:
        activeGroups.includes(ONETRUST_CATEGORIES.FUNCTIONAL) &&
        activeGroups.includes(ONETRUST_CATEGORIES.TARGETING),
      activeGroups,
    };
    setConsentState(newState);
  }, []);

  // initial check on mount
  useEffect(() => {
    updateConsentState();
  }, []);

  // update state on mount and when consent changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("OneTrustGroupsUpdated", updateConsentState);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("OneTrustGroupsUpdated", updateConsentState);
      }
    };
  }, [updateConsentState]);

  return consentState;
};
